import { useState } from "react";
import { Check, Globe, User, Mail, ShieldCheck } from "lucide-react";
import Navigation from "../components/Navigation";
import TwoFactorAuthCard from "../components/TwoFactorAuthCard";
import { useAuth } from "../contexts/AuthContext";
import { useCurrency } from "../contexts/CurrencyContext";

/**
 * Account Profile page. Lets the authenticated user permanently lock in
 * their billing currency (writes to `user.preferred_currency` via the
 * CurrencyContext, which PUTs /api/currency/preference). Also surfaces the
 * resolved-currency-source so the user understands where the active value
 * came from (in-app toggle / saved profile / GeoIP / default).
 */
export default function AccountProfile() {
  const { user } = useAuth();
  const {
    currency, symbol, name, source, supported, setCurrency, rate,
    rates_source, rates_age_seconds,
  } = useCurrency();
  const [savedCcy, setSavedCcy] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSave = async (code) => {
    setSaving(true);
    try {
      await setCurrency(code);
      setSavedCcy(code);
      window.setTimeout(() => setSavedCcy(null), 2500);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--rl-bg)]" data-testid="account-profile-page">
      <Navigation />
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-[var(--rl-text-secondary)] mb-2">
            Account
          </div>
          <h1 className="text-4xl font-display font-black tracking-tight">Profile & Preferences</h1>
        </div>

        {/* Identity */}
        <section className="border border-[var(--rl-border)] bg-white p-6 mb-6" data-testid="profile-identity-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold mb-4">
            <User className="w-4 h-4 text-[var(--rl-brand)]" /> Identity
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--rl-text-secondary)] mb-1">Email</div>
              <div className="text-sm font-mono flex items-center gap-2" data-testid="profile-email">
                <Mail className="w-3 h-3" />
                {user?.email || "—"}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--rl-text-secondary)] mb-1">Role</div>
              <div className="text-sm font-semibold capitalize flex items-center gap-2" data-testid="profile-role">
                <ShieldCheck className="w-3 h-3" /> {user?.role || "—"}
              </div>
            </div>
          </div>
        </section>

        {/* Two-Factor Authentication */}
        <TwoFactorAuthCard />

        {/* Billing currency */}
        <section className="border border-[var(--rl-border)] bg-white p-6" data-testid="profile-billing-currency-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold mb-2">
            <Globe className="w-4 h-4 text-[var(--rl-brand)]" /> Billing Currency
          </div>
          <p className="text-sm text-[var(--rl-text-secondary)] mb-4 leading-relaxed">
            Choose the default currency you want to see across the wizard, dashboards, and Stripe checkout.
            The platform converts our internal USD pricing at the latest cached exchange rate.
          </p>
          <div className="text-[11px] text-[var(--rl-text-secondary)] mb-5 font-mono" data-testid="profile-currency-meta">
            <span className="font-bold">Active:</span> {currency} ({name}) {symbol} ·{" "}
            <span className="font-bold">Source:</span> {source} ·{" "}
            <span className="font-bold">USD→{currency}:</span> {Number(rate || 0).toFixed(4)} ·{" "}
            <span className="font-bold">FX feed:</span> {rates_source || "static-fallback"}
            {typeof rates_age_seconds === "number" && (
              <> · <span className="font-bold">Refreshed:</span> {Math.floor(rates_age_seconds / 60)}m ago</>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {(supported || []).map((c) => {
              const isActive = c.code === currency;
              const justSaved = c.code === savedCcy;
              return (
                <button
                  key={c.code}
                  type="button"
                  disabled={saving}
                  onClick={() => handleSave(c.code)}
                  className={`flex items-center justify-between gap-2 px-4 py-3 border transition-colors text-left ${isActive ? "bg-[var(--rl-brand)] text-white border-[var(--rl-brand)]" : "bg-white text-[var(--rl-text)] border-[var(--rl-border)] hover:border-[var(--rl-text)]"} ${saving ? "opacity-60 cursor-wait" : ""}`}
                  data-testid={`profile-currency-${c.code}`}
                >
                  <div>
                    <div className="font-mono text-xs font-bold">{c.code}</div>
                    <div className="text-[11px] opacity-80">{c.name}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-sm">{c.symbol}</span>
                    {justSaved && <Check className="w-4 h-4" data-testid={`profile-currency-${c.code}-saved`} />}
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
